/**
 * StoryboardDetail — one disciplined-compose scene as a storyboard: a large preview FRAME (the
 * archetype visual) with the builder payload as NOTES around it (archetype, narration, the
 * act-by-act shot list with on-screen text). Every note carries a 💬 to anchor a comment;
 * comments render inline (Frame.io-style) and queue as instructions for the next outline round.
 */
import { useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { Sparkles, Volume2 } from 'lucide-react';
import type { SlideOutline, StoryboardMeta } from '@/types/outline';
import { parseStoryboardPayload, type ParsedAct } from '@/lib/parseStoryboardPayload';
import { archetypePreviewUrl, TIER_BADGE_STYLE, TIER_LABEL } from './archetypePreview';
import { MiniAudioPlayer } from './MiniAudioPlayer';
import { SeamEditor } from './SeamEditor';
import { Commentable, SectionLabel } from './Commentable';
import { useOutlineEditQueueStore, type OutlineEditItem } from '@/stores/outlineEditQueueStore';

interface Props {
  slide: SlideOutline;
  storyboard?: StoryboardMeta;
  conversationId?: string;
  sceneNumber: number;
  onSwitchArchetype?: () => void;
  /** Optimistic archetype after a switch — shown immediately with a "Pending" badge until re-plan. */
  pendingArchetypeKey?: string;
  // Per-slide split audio (from split_master_audio / TTS) — the slice the builder receives.
  slideDbId?: string;
  /** Current audio version — threaded into the player so a re-narrated slide busts the cache. */
  audioVersion?: number | null;
  audioDuration?: number | null;
  hasAudio?: boolean;
  playingSlideId?: string | null;
  onAudioPlayChange?: (slideId: string, playing: boolean) => void;
  /** Master audio URL for previewing the window in the seam editor. */
  seamAudioUrl?: string;
  isReadOnly?: boolean;
}

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

function cleanActBody(act: ParsedAct): string[] {
  return act.body
    .replace(/^\s*(ACT\s+\d+|PAGE\s+\d+)\b/i, '')
    .replace(/\(~?[^)]*\d[^)]*\)/, '')
    .replace(/\[actKind:[^\]]*\]/gi, '')
    .replace(/onScreenText:\s*"[^"]+"/gi, '')
    .split('|')
    .map((s) => s.replace(/\s+/g, ' ').trim())
    .filter((s) => s.length > 1);
}

export function StoryboardDetail({ slide, storyboard, conversationId, sceneNumber, onSwitchArchetype, pendingArchetypeKey, slideDbId, audioVersion, audioDuration, hasAudio, playingSlideId, onAudioPlayChange, seamAudioUrl, isReadOnly }: Props) {
  const parsed = useMemo(() => parseStoryboardPayload(slide, storyboard), [slide, storyboard]);
  const narration = slide.scriptSegment?.trim();
  const sceneLabel = `scene ${sceneNumber}${slide.title ? ` ("${slide.title}")` : ''}`;
  // Optimistic: the pending archetype's preview shows immediately after a switch.
  const displayArchetypeKey = pendingArchetypeKey || parsed.archetypeKey || parsed.lessonFrame;
  // Archetype scenes (Kubicle) show the archetype frame + tier; general/audio-first scenes
  // fall back to the template layout preview + name (the "design intent" for that scene).
  const hasArchetype = !!displayArchetypeKey || !!storyboard;
  const tier = (storyboard?.tier || parsed.tier || 'A') as string;
  // A generated picture is the frame, unless an archetype switch is waiting to show.
  const sceneImage = pendingArchetypeKey ? undefined : slide.storyboardImage?.url;
  const displayLabel = sceneImage ? 'Storyboard image' : displayArchetypeKey || slide.layoutName || '—';
  const previewUrl = sceneImage ?? (displayArchetypeKey ? archetypePreviewUrl({ archetypeKey: displayArchetypeKey }) : slide.layoutThumbnail);
  const [frameFailed, setFrameFailed] = useState(false);
  // A picture's link expires after an hour; the next outline read brings a fresh one.
  useEffect(() => { setFrameFailed(false); }, [previewUrl]);

  const queue = useOutlineEditQueueStore((s) => (conversationId ? s.queuesByConversation[conversationId] : undefined));
  const commentsByAnchor = useMemo(() => {
    const out: Record<string, OutlineEditItem[]> = {};
    for (const it of queue ?? []) {
      if (it.kind === 'comment' && it.slideId === slide.id && it.anchorId) (out[it.anchorId] ??= []).push(it);
    }
    return out;
  }, [queue, slide.id]);

  const ctx = (anchorId: string, anchorLabel: string) => ({
    anchorId, anchorLabel, sceneLabel, conversationId, slideId: slide.id,
    comments: commentsByAnchor[anchorId] ?? [],
  });

  return (
    <div className="flex flex-col lg:flex-row gap-4">
      {/* The FRAME — the archetype visual, prominent */}
      <div className="lg:w-[44%] flex-shrink-0">
        <div className={cn('relative aspect-video rounded-xl overflow-hidden bg-gray-100 dark:bg-zinc-800 ring-1 group/frame', pendingArchetypeKey ? 'ring-amber-400/70 dark:ring-amber-400/50' : 'ring-black/5 dark:ring-white/10')}>
          {previewUrl && !frameFailed ? (
            <img src={previewUrl} alt={displayLabel} className="w-full h-full object-cover" loading="lazy" onError={() => setFrameFailed(true)} />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-xs text-gray-400 dark:text-zinc-500 font-mono">{displayLabel}</div>
          )}
          {pendingArchetypeKey && (
            <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-400 text-amber-950 text-[10px] font-semibold shadow-sm">
              Pending · not assigned yet
            </span>
          )}
          {onSwitchArchetype && (
            <button
              onClick={onSwitchArchetype}
              className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-black/55 text-white text-[11px] font-medium opacity-0 group-hover/frame:opacity-100 transition-opacity hover:bg-black/70"
            >
              <Sparkles className="w-3 h-3" /> Switch archetype
            </button>
          )}
        </div>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[12px] text-gray-800 dark:text-zinc-200">{displayLabel}</span>
          {pendingArchetypeKey && <span className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">pending re-plan</span>}
          {hasArchetype && <span className={cn('inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium', TIER_BADGE_STYLE[tier] ?? TIER_BADGE_STYLE.A)}>{TIER_LABEL[tier] ?? `Tier ${tier}`}</span>}
          {(storyboard?.surface || parsed.surface) && <span className="text-[11px] text-gray-400 dark:text-zinc-500">{storyboard?.surface || parsed.surface}</span>}
        </div>

        {/* Narration + split audio sit under the frame so the left column reads as the "shot" */}
        {(narration || hasAudio || slide.audioRange) && (
          <div className="mt-2 text-[13px]">
            <Commentable {...ctx('narration', 'Narration')}>
              <div className="flex items-center gap-2">
                <SectionLabel>Narration</SectionLabel>
                {slide.audioRange && (
                  <span className="text-[10px] tabular-nums text-gray-400 dark:text-zinc-500">
                    split {fmt(slide.audioRange.startTime)}–{fmt(slide.audioRange.endTime)} · {fmt(slide.audioRange.endTime - slide.audioRange.startTime)}
                  </span>
                )}
              </div>
              {narration && <p className="text-gray-700 dark:text-zinc-300 leading-snug italic">"{narration}"</p>}
              {hasAudio && slideDbId ? (
                <div className="mt-1.5">
                  <MiniAudioPlayer slideId={slideDbId} version={audioVersion} audioDuration={audioDuration} isPlaying={playingSlideId === slideDbId} onPlayChange={(playing) => onAudioPlayChange?.(slideDbId, playing)} />
                </div>
              ) : (
                <div className="mt-1.5 flex items-center gap-1.5 text-[10px] text-gray-400 dark:text-zinc-500"><Volume2 className="w-3 h-3" /> Audio pending</div>
              )}
            </Commentable>
          </div>
        )}

        {/* Guided split correction — only when this scene has an outgoing seam (not the last scene) */}
        {slide.seam && (
          <SeamEditor
            conversationId={conversationId}
            beforeSlideId={slide.id}
            seam={slide.seam}
            seamAudioUrl={seamAudioUrl}
            isReadOnly={isReadOnly}
          />
        )}
      </div>

      {/* The NOTES — builder payload, each item commentable */}
      <div className="flex-1 min-w-0 space-y-2 text-[13px]">
        {parsed.intro && (() => {
          const segs = parsed.intro.split('|').map((s) => s.replace(/\s+/g, ' ').trim()).filter((s) => s.length > 1);
          return (
            <Commentable {...ctx('intro', 'Scene direction')}>
              <SectionLabel>Scene direction</SectionLabel>
              {segs.length > 1 ? (
                <ul className="space-y-0.5 text-gray-600 dark:text-zinc-400 leading-snug list-disc list-inside marker:text-gray-300 dark:marker:text-zinc-600">
                  {segs.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              ) : (
                <p className="text-gray-600 dark:text-zinc-400 leading-snug">{parsed.intro}</p>
              )}
            </Commentable>
          );
        })()}

        {/* Other builder-read fields — shown only when populated (most disciplined scenes leave these empty) */}
        {slide.keyPoints && slide.keyPoints.length > 0 && (
          <Commentable {...ctx('keyPoints', 'Key points')}>
            <SectionLabel>Key points</SectionLabel>
            <ul className="space-y-0.5 text-gray-600 dark:text-zinc-400 leading-snug list-disc list-inside marker:text-gray-300 dark:marker:text-zinc-600">
              {slide.keyPoints.map((k, i) => <li key={i}>{k}</li>)}
            </ul>
          </Commentable>
        )}
        {slide.audienceTakeaway && (
          <Commentable {...ctx('takeaway', 'Audience takeaway')}>
            <SectionLabel>Audience takeaway</SectionLabel>
            <p className="text-gray-600 dark:text-zinc-400 leading-snug">{slide.audienceTakeaway}</p>
          </Commentable>
        )}
        {slide.narrativeFlow && (slide.narrativeFlow.position || slide.narrativeFlow.comesFrom || slide.narrativeFlow.leadsTo) && (
          <Commentable {...ctx('flow', 'Narrative flow')}>
            <SectionLabel>Narrative flow</SectionLabel>
            <div className="text-gray-600 dark:text-zinc-400 leading-snug space-y-0.5">
              {slide.narrativeFlow.position && <div><span className="text-gray-400 dark:text-zinc-500">position:</span> {slide.narrativeFlow.position}</div>}
              {slide.narrativeFlow.comesFrom && <div><span className="text-gray-400 dark:text-zinc-500">builds on:</span> {slide.narrativeFlow.comesFrom}</div>}
              {slide.narrativeFlow.leadsTo && <div><span className="text-gray-400 dark:text-zinc-500">sets up:</span> {slide.narrativeFlow.leadsTo}</div>}
            </div>
          </Commentable>
        )}

        {parsed.acts.length > 0 && (
          <div>
            <SectionLabel>Shot list · sent to the builder</SectionLabel>
            <div className="space-y-1">
              {parsed.acts.map((act, i) => {
                const segs = cleanActBody(act);
                return (
                  <Commentable key={i} {...ctx(`act-${i}`, act.label)}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[12px] font-semibold text-primary dark:text-accent-foreground">{act.label}</span>
                      {act.window && <span className="text-[11px] text-gray-400 dark:text-zinc-500 tabular-nums">{act.window}</span>}
                      {act.actKind && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-zinc-800 text-gray-500 dark:text-zinc-400">{act.actKind}</span>}
                    </div>
                    {segs.length > 0 && (
                      <ul className="mt-0.5 space-y-0.5 text-gray-600 dark:text-zinc-400 leading-snug list-disc list-inside marker:text-gray-300 dark:marker:text-zinc-600">
                        {segs.map((s, j) => <li key={j}>{s}</li>)}
                      </ul>
                    )}
                    {act.onScreenText.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {act.onScreenText.map((t, j) => (
                          <span key={j} className="text-[11px] px-1.5 py-0.5 rounded bg-accent dark:bg-primary/10 text-accent-foreground">"{t}"</span>
                        ))}
                      </div>
                    )}
                  </Commentable>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
